<script setup lang="ts">
import { computed } from 'vue';

import type { Agent } from '@/types';

const VISIBLE_AGENTS_COUNT = 3;

const { agents } = defineProps<{ agents: Agent[] }>();

const visibleAgents = computed(() =>
  agents.filter(({ avatarUrl }) => avatarUrl).slice(0, VISIBLE_AGENTS_COUNT),
);
// Agents without an avatar are never shown, but still count towards the team size.
const hiddenAgentsCount = computed(() => agents.length - visibleAgents.value.length);
</script>

<template>
  <div :class="$style.agencyAgents">
    <div
      v-for="(agent, index) in visibleAgents"
      :key="`${agent.avatarUrl}-${index}`"
      :class="$style.agencyAgentAvatar"
    >
      <img :src="agent.avatarUrl" alt="" />
    </div>
    <div
      v-if="hiddenAgentsCount"
      :class="$style.agencyAgentAvatarPlaceholder"
      data-test="hidden-agents-count"
    >
      +{{ hiddenAgentsCount }}
    </div>
  </div>
</template>

<style lang="scss" module>
@use '@/assets/variables' as *;

.agencyAgents {
  display: flex;
  align-items: center;
  padding-top: $space-200;

  @media (max-width: $viewport-sm-min) {
    padding-top: $space-150;
  }
}

.agencyAgentAvatar,
.agencyAgentAvatarPlaceholder {
  position: relative;
  flex-shrink: 0;
  width: $space-400;
  height: $space-400;
  margin-left: -$space-150;
  overflow: hidden;
  background-color: $color-grey-30;
  border: 2px solid $color-white;
  border-radius: 50%;

  &:first-child {
    margin-left: 0;
  }
}

.agencyAgentAvatar {
  img {
    width: 100%;
    height: 100%;
    object-fit: cover;
  }
}

.agencyAgentAvatarPlaceholder {
  display: flex;
  align-items: center;
  justify-content: center;

  @include font-bold;

  color: $color-grey-90;
  background-color: $color-grey-30;
}
</style>
